import React, { useState, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useMutation, useQuery } from '@apollo/client'
import { useSession } from '@slerp/accounts'
import {
  message,
  Button,
  Col,
  Row,
  Form,
  Alert,
  PageHeader,
  Skeleton,
  Tooltip
} from 'antd'
import DatePicker from 'components/Common/DayjsDatePicker'
import { orderAbilities } from 'components/Orders/Info/utils'
import { EditOutlined } from '@ant-design/icons'
import {
  UPDATE_ORDER_TIMESLOT,
  GET_STORE_TIMESLOTS
} from 'components/Orders/OrderQueries'
import { OrderInfo } from 'components/Orders/Info/type'
import { Order, PreOrderSettings } from 'components/Orders/types'
import {
  disabledDate,
  formatInWrittenOffset
} from 'components/Orders/Helpers'
import { DeliveryCompleteCTA } from 'components/Orders/Info/CallToAction'
import styled from '@emotion/styled'

const { useForm } = Form

export interface Timeslot {
  value: string
  range: string
}

interface FormTypes {
  fulfillmentDate?: Dayjs
}

interface FulfillmentTimeSlotProps {
  order: OrderInfo
  successCallback: () => void
}

const FulfillmentTimeSlot = ({
  order,
  successCallback
}: FulfillmentTimeSlotProps) => {
  const { user } = useSession()
  const { transaction_id, store, merchant } = order
  const { canManageFulfillmentDate } = orderAbilities(order)
  const [form] = useForm()
  const [showForm, setShowForm] = useState<boolean>(false)
  const [newFulfillmentDate, setNewFulfillmentDate] = useState<Date | null>(
    dayjs().toDate()
  )
  const [timeslots, setTimeslots] = useState<Timeslot[]>([])
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>('')

  const [updateOrderTimeslot] = useMutation(UPDATE_ORDER_TIMESLOT, {
    fetchPolicy: 'no-cache'
  })

  const isPickup = order.fulfillment_type === 'pickup'
  const deliveryAddress = isPickup
    ? undefined
    : [
        order.customer_details?.address?.flat_number,
        order.customer_details?.address?.line_1,
        order.customer_details?.address?.city,
        order.customer_details?.address?.zip
      ]
        .filter(Boolean)
        .join(', ') || undefined

  const { data: timeslotsData, loading: timeslotsLoading } = useQuery(
    GET_STORE_TIMESLOTS,
    {
      skip: !newFulfillmentDate,
      fetchPolicy: 'no-cache',
      variables: {
        storeId: store.id,
        fulfillmentDate: newFulfillmentDate
          ? dayjs(newFulfillmentDate).format('YYYY-MM-DD')
          : '',
        fulfillmentType: order.fulfillment_type,
        address: deliveryAddress
      }
    }
  )

  useEffect(() => {
    const slots: { value: string; startTime: string; endTime: string }[] = (
      timeslotsData?.getValidStore?.fulfillmentTimeslots ?? []
    ).filter((s: { value: string }) => s.value !== 'asap')
    const mapped = slots.map((s) => ({
      value: s.value,
      range: `${s.startTime?.slice(0, 5)} - ${s.endTime?.slice(0, 5)}`
    }))
    setTimeslots(mapped)
    setSelectedTimeslot(mapped[0]?.value || '')
  }, [timeslotsData])

  const updateOrderTimeslotHandler = (values: FormTypes) => {
    const { fulfillmentDate } = values

    if (!selectedTimeslot) {
      message.error('Please select a timeslot', 1)
      return
    }

    updateOrderTimeslot({
      variables: {
        transactionId: transaction_id,
        fulfillmentDate:
          fulfillmentDate?.format('YYYY-MM-DD') ||
          dayjs(newFulfillmentDate).format('YYYY-MM-DD'),
        fulfillmentTime: formatInWrittenOffset(selectedTimeslot, 'HH:mm'),
        userId: user?.id
      }
    })
      .then(() => {
        message.destroy()
        message.success(
          `Timeslot has been updated for order ${transaction_id}!`,
          1
        )
        successCallback()
      })
      .catch((error) => {
        message.destroy()
        message.error(`${error.message}`, 1)
      })
  }

  return (
    <>
      {showForm ? (
        <Form
          form={form}
          // ref={formRef}
          onFinish={updateOrderTimeslotHandler}
        >
          <PageHeader title='Change Fulfilment Time' />
          <Row gutter={[8, 16]}>
            <Col>
              <Form.Item name='fulfillmentDate' label='Date'>
                <DatePicker
                  format='DD-MM-YYYY'
                  disabledDate={(value) =>
                    disabledDate(
                      value.toDate(),
                      dayjs().toDate(),
                      merchant
                        ?.pre_order_settings[0] as unknown as PreOrderSettings,
                      order as unknown as Partial<Order>
                    )
                  }
                  defaultValue={dayjs(newFulfillmentDate)}
                  value={dayjs(newFulfillmentDate)}
                  placeholder='Start date'
                  style={{ width: 170 }}
                  onChange={(value) => {
                    setNewFulfillmentDate(value ? value.toDate() : value)
                  }}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name='fulfillmentTime' label='Time'>
                {timeslotsLoading ? (
                  <Skeleton.Input active style={{ width: 160 }} />
                ) : (
                  /* had issues with changing select value when date is changed when I used antd's Select component */
                  <select
                    name='fulfillmentTime'
                    style={{
                      padding: '4px 11px',
                      borderColor: '#d9d9d9',
                      background: 'white',
                      borderRadius: '2px'
                    }}
                    defaultValue={''}
                    value={selectedTimeslot}
                    onChange={(e) => setSelectedTimeslot(e.target.value)}
                    required
                  >
                    <option key='disabledOption' value={''}>
                      {timeslots?.length > 0
                        ? 'Select timeslot'
                        : 'No available timeslots'}
                    </option>
                    {timeslots &&
                      timeslots.length > 0 &&
                      timeslots.map((slot: Timeslot, index: number) => (
                        <option key={index} value={slot.value}>
                          {slot.range}
                        </option>
                      ))}
                  </select>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Alert
              message='Note, if you use SlerpComposer and have already imported your order, you need to change the date in SlerpComposer as well.'
              description={
                <>
                  <span>
                    To learn more about Composer please click{' '}
                    <a
                      target='_blank'
                      rel='noopener noreferrer'
                      href='https://www.slerp.com/project/ordermanagement-composer/'
                    >
                      here
                    </a>
                  </span>
                </>
              }
              type='warning'
              showIcon
            />
          </Row>
          <Row justify='end' gutter={16} style={{ margin: 10 }}>
            <Col>
              <Button
                className='-secondary _uppercase'
                type='ghost'
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              {!newFulfillmentDate ||
              !selectedTimeslot ||
              !canManageFulfillmentDate ? (
                <Tooltip title='This functionality is not available anymore. Please contact Customer Support for further assistance.'>
                  <Button
                    className='-secondary _uppercase'
                    key='submit'
                    htmlType='submit'
                    disabled={true}
                  >
                    Submit
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  className='-secondary _uppercase'
                  key='submit'
                  htmlType='submit'
                >
                  Submit
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      ) : (
        <ButtonBox>
          {canManageFulfillmentDate ? (
            <Button icon={<EditOutlined />} onClick={() => setShowForm(true)}>
              Change fulfillment time
            </Button>
          ) : (
            <Tooltip title='This functionality is not available anymore. Please contact Customer Support for further assistance.'>
              <Button icon={<EditOutlined />} type='primary' disabled={true}>
                Change fulfillment time
              </Button>
            </Tooltip>
          )}
          <DeliveryCompleteCTA
            order={order}
            successCallback={successCallback}
          />
        </ButtonBox>
      )}
    </>
  )
}

const ButtonBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export default FulfillmentTimeSlot
